<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="680px"
    overlay-opacity="0.94"
    content-class="glass-dialog"
  >
    <v-card class="modern-card" elevation="0">
      <!-- Header with animated close button -->
      <div class="dialog-header">
        <h2 class="dialog-title">Create New Business</h2>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          x-large
          depressed
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Main content with floating elements -->
      <v-card-text class="dialog-content">
        <!-- Logo upload with preview -->
        <div class="logo-upload-section">
          <editable-avatar
            :image="profile.image"
            :onImageSelected="onImageSelected"
            size="140"
            class="logo-preview floating-element"
          />
          <div class="upload-hint">Click to upload logo</div>
        </div>

        <!-- Form with animated inputs -->
        <v-form ref="form" class="business-form">
          <div class="form-row">
            <v-text-field
              v-model="profile.name"
              label="Business Name"
              outlined
              color="accent"
              hide-details="auto"
              class="modern-input"
              :rules="[rules.required]"
            >
              <template v-slot:prepend-inner>
                <v-icon small color="accent">mdi-domain</v-icon>
              </template>
            </v-text-field>
          </div>

          <div class="form-row">
            <v-text-field
              v-model="profile.website"
              label="Website"
              outlined
              color="accent"
              hide-details="auto"
              class="modern-input"
              :rules="[rules.required, rules.url]"
            >
              <template v-slot:prepend-inner>
                <v-icon small color="accent">mdi-web</v-icon>
              </template>
            </v-text-field>
          </div>

          <div class="form-row">
            <v-textarea
              v-model="profile.about"
              label="About your business"
              outlined
              color="accent"
              hide-details="auto"
              rows="3"
              auto-grow
              class="modern-textarea"
              :rules="[rules.required]"
              counter="250"
            >
              <template v-slot:prepend-inner>
                <v-icon small color="accent">mdi-information-outline</v-icon>
              </template>
            </v-textarea>
          </div>
        </v-form>

        <!-- Error message with smooth transition -->
        <transition name="slide-fade">
          <v-alert
            v-if="message"
            type="error"
            dense
            outlined
            class="error-alert"
          >
            {{ message }}
          </v-alert>
        </transition>
      </v-card-text>

      <!-- Floating action buttons -->
      <v-card-actions class="dialog-actions">
        <v-btn
          text
          large
          @click="onClose"
          class="cancel-btn"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          large
          @click="onSave"
          :loading="loading"
          depressed
          class="submit-btn"
          dark
        >
          <span>Create Business</span>
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";

export default {
  components: { EditableAvatar },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    volunteer: Object,
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      loading: false,
      rules: {
        required: value => !!value || "Required",
        url: value => {
          const pattern = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
          return pattern.test(value) || "Enter a valid URL";
        },
        max250: v => (v && v.length <= 250) || "Max 250 characters"
      },
      selectedImage: null,
      profile: {
        name: "",
        website: "",
        about: "",
        image: null
      }
    };
  },
  methods: {
    ...mapActions("business", {
      addBusiness: "addBusiness"
    }),
    onSave() {
      if (this.$refs.form.validate()) {
        this.message = null;
        const formData = new FormData();
        
        if (this.selectedImage) {
          formData.append("image", this.selectedImage);
        }
        formData.append("name", this.profile.name);
        formData.append("about", this.profile.about);
        formData.append("website", this.profile.website);
        
        this.loading = true;
        this.addBusiness(formData)
          .then(() => {
            this.loading = false;
            this.onClose();
            this.resetForm();
          })
          .catch(error => {
            this.loading = false;
            this.message = 
              error.response?.data?.message ||
              error.message ||
              "An error occurred";
          });
      }
    },
    onImageSelected(image) {
      this.selectedImage = image;
      this.message = null;
    },
    resetForm() {
      this.profile = {
        name: "",
        website: "",
        about: "",
        image: null
      };
      this.selectedImage = null;
      this.$refs.form.resetValidation();
    }
  },
  watch: {
    dialog(val) {
      if (!val) {
        this.resetForm();
      }
    }
  }
};
</script>

<style scoped lang="scss">
/* Glass morphism effect */
.glass-dialog {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.modern-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18) !important;
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background: linear-gradient(135deg,#084468,#3b7ca1);
  color: white;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.5px;
  margin: 0;
}

.close-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  
  &:hover {
    transform: rotate(90deg);
    background: rgba(255, 255, 255, 0.2) !important;
  }
}

.dialog-content {
  padding: 32px;
}

.logo-upload-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
}

.logo-preview {
  transition: all 0.3s ease;
  border: 2px dashed rgba(98, 0, 238, 0.3);
  margin-bottom: 12px;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(98, 0, 238, 0.15);
  }
}

.upload-hint {
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
}

.business-form {
  .form-row {
    margin-bottom: 24px;
  }
}

.modern-input,
.modern-textarea {
  border-radius: 12px !important;
  
  ::v-deep .v-input__slot {
    transition: all 0.3s ease;
    background: rgba(245, 245, 245, 0.7) !important;
    
    &:hover {
      background: rgba(245, 245, 245, 0.9) !important;
    }
    
    fieldset {
      border-width: 1px !important;
      border-color: rgba(0, 0, 0, 0.1) !important;
    }
  }
  
  ::v-deep .v-label {
    color: #666 !important;
    font-size: 0.9rem;
  }
}

.error-alert {
  margin-top: 16px;
  border-radius: 8px !important;
  border-left: 4px solid #ff5252 !important;
}

.dialog-actions {
  padding: 16px 32px;
  background: rgba(245, 245, 245, 0.7);
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.cancel-btn {
  color: #666 !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  
  &:hover {
    color: #333 !important;
  }
}

.submit-btn {
  background: linear-gradient(135deg,#084468,#3b7ca1) !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(98, 0, 238, 0.2);
  transition: all 0.3s ease;
  padding: 0 24px !important;
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(98, 0, 238, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
}

/* Animations */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.floating-element {
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
  100% {
    transform: translateY(0px);
  }
}
</style>